<template>
  <main class="main main--has-floating-buttons grid--dense" style="grid-auto-rows: minmax(20rem, max-content);">
    <spinner-overlay v-if="creating" />
    <spinner-overlay v-if="loading" />
    <card v-if="!hasLicense('EU_DE_Registrierkasse')" title="Allgemeines" cols="12">
      <hm-card-inner :full-size="true">
        <hm-col cols="12">
          <span class="info-text info-text--full-size info-text--no-margin col-12">Nach dem 31. März 2021 ist die Inverkehrbringung von Kassensystemen, ohne zertifizierte TSE, verboten. Die Erfassung von teilweise baren Zahlungsvorgängen ist, ab diesem Zeitpunkt, nur noch mit aktivierter TSE möglich.</span>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner>
        <hm-col cols="12">
          <quote cite="Quelle: 1.2 Abs. 1 AEAO zu § 146a AO (Stand: 21. März 2021)">
            <p>Kassenfunktion haben elektronische Aufzeichnungssysteme dann, wenn diese der <b>Erfassung</b> und Abwicklung von <b>zumindest teilweise baren Zahlungsvorgängen</b> dienen können. Dies <b>gilt auch für</b> vergleichbare elektronische, <b>vor Ort genutzte</b> Zahlungsformen (Elektronisches Geld wie z. B. <b>Geldkarte</b>, <b>virtuelle Konten</b> oder Bonuspunktesysteme von Drittanbietern) sowie an Geldes statt angenommener <b>Gutscheine</b>, <b>Guthabenkarten</b>, <b>Bons und dergleichen</b>.</p>
          </quote>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner>
        <hm-col cols="12">
          <p>Bitte führen Sie alle nachfolgenden Schritte aus um die Einrichtung der TSE abzuschließen.</p>
        </hm-col>
      </hm-card-inner>
    </card>
    <card cols="12" title="Schritt 1: Offene Folios abschließen">
      <hm-card-inner v-if="step1Complete">
        <hm-col cols="12" style="position: relative;text-align:center;">
          <i class="fal fa-check fa-w-14 fa-9x color--mint"></i>
        </hm-col>
      </hm-card-inner>
      <template v-else>
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">Tipp: Wenn Sie beim Klick auf das Folio die STRG-Taste auf der Tastatur gedrückt halten, wird das Folio in einem neuen Tab geöffnet.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Änderungen haben möglicherweise steuerliche Implikationen für Vorperioden. Kontaktieren Sie bitte unseren Support, wenn Sie <b>alle</b> aufgelisteten Geschäftsfälle, permanent und unwiderruflich, im möglicherweiße fehlerhaften Zustand, festschreiben möchten..</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner :full-size="true">
          <folio-table :folios="folios" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-col cols="12">
            <p><b>Warum werden die Folios in der Liste angezeigt</b></p>
            <p>Dies kann folgende Gründe haben:</p>
            <ol>
              <li>Es wurde zwar eine Zahlung erfasst aber keine Rechnung erstellt</li>
              <li>Es wurde zwar die Rechnung storniert, nicht aber die Zahlung</li>
              <li>Es wurde zwar die Zahlung storniert, nicht aber die Rechnung</li>
              <li>Mit den abgeschlossenen Zahlungsvorgängen wurde nur ein Teil der Rechnungen bezahlt</li>
            </ol>
            <p><b>Wie behebe ich Fall 1 ("Es wurde zwar eine Zahlung erfasst aber keine Rechnung erstellt")</b></p>
            <p>Öffnen Sie das Folio, und wenn der Geschäftsfall so stattgefunden hat, klicken Sie auf "Rechnung erstellen".</p>
            <p>Wenn der Geschäftsfall so nicht stattgefunden hat, so stornieren Sie bitte die Zahlung(en) und löschen sämtliche Leistungen vom Folio.</p>

            <p><b>Wie behebe ich Fall 2 ("Es wurde zwar die Rechnung storniert, nicht aber die Zahlung")</b></p>
            <p>Öffnen Sie das Folio, und wenn der Geschäftsfall so stattgefunden hat, klicken Sie auf "Rechnung erstellen".</p>
            <p>Wenn der Geschäftsfall so nicht stattgefunden hat, so stornieren Sie bitte die Zahlung(en) und löschen sämtliche Leistungen vom Folio.</p>

            <p><b>Wie behebe ich Fall 3 ("Es wurde zwar die Zahlung storniert, nicht aber die Rechnung")</b></p>
            <p>Öffnen Sie das Folio, und wenn der Geschäftsfall so stattgefunden hat, fügen Sie bitte die Zahlung hinzu und klicken auf "Zahlugnsvorgang abschließen".</p>
            <p>Wenn der Geschäftsfall so nicht stattgefunden hat, so stornieren Sie bitte die Rechnung und löschen sämtliche Leistungen vom Folio.</p>

            <p><b>Wie behebe ich Fall 4 ("Mit den abgeschlossenen Zahlungsvorgängen wurde nur ein Teil der Rechnungen bezahlt")</b></p>
            <p>Hierbei kann es sich um einen validen Geschäftsfall, wie z.B. eine An- oder Teilzahlung einer Rechnung handeln.</p>
            <p>Da diese aber im Bezug auf die KassenSichV (Kassensicherheitsverordnung) anders zu handhaben sind, müssen diese Geschäftsfälle neu erfasst werden.</p>
            <p>Gehen Sie dazu bitte wie folgt vor</p>
            <ol>
              <li>Folio öffnen</li>
              <li>Zahlung stornieren (notieren Sie sich zuvor den Zahlungsbetrag und die Zahlungsart)</li>
              <li>Zahlung hinzufügen</li>
              <li>"Als Anzahlung verbuchen" klicken</li>
              <li>Den zu verwendenden Mehrwertsteuer-Satz für die Anzahlungsrechnung auswählen</li>
              <li>Mit "Okay" bestätigen</li>
            </ol>

            <p><b>Wie kann ich solche Fälle in Zukunft vermeiden?</b></p>
            <p>Bitte kontrollieren Sie regelmäßig den Bericht "Offene Geschäftsfälle".</p>
          </hm-col>
        </hm-card-inner>
      </template>
    </card>
    <card title="Schritt 2: TSE Einrichten" cols="12">
      <hm-card-inner v-if="!step1Complete">
        <hm-col cols="12">
          <p>Bitte schließen Sie den vorhergehenden Schritt ab.</p>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-else-if="step2Complete">
        <hm-col cols="12" style="position: relative;text-align:center;">
          <i class="fal fa-check fa-w-14 fa-9x color--mint"></i>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-else>
        <hm-col cols="12">
          <p><b>Bitte kontaktieren Sie den Support, damit dieser die TSE für Sie vorbereitet.</b></p>
        </hm-col>
      </hm-card-inner>
    </card>
    <card title="Schritt 3: Registrierkasse aktivieren" cols="12">
      <hm-card-inner v-if="!step1Complete || !step2Complete">
        <hm-col cols="12">
          <p>Bitte schließen Sie die vorhergehenden Schritte ab.</p>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-else-if="step3Complete">
        <hm-col cols="12" style="position: relative;text-align:center;">
          <i class="fal fa-check fa-w-14 fa-9x color--mint"></i>
        </hm-col>
      </hm-card-inner>
      <template v-else>
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Bitte stellen Sie sicher, dass <b>kein andererer Benutzer</b> Barzahlungen erfasst, während die Registrierkasse aktiviert wird. Andernfalls kann es zu <b><u>irreparablen</u></b> Datenfehlern führen.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-col cols="12">
            <p v-if="!hasRole('ROLE_ADMIN')">Dieser Schritt kann nur von Benutzer mit <b>Administratorpriviliegen</b> ausgeführt werden.</p>
          </hm-col>
          <template v-if="hasRole('ROLE_ADMIN')" />
          <hm-input-money v-model="anfangsbestand"
                          :currency="hotelSettings.details.currency"
                          label="Anfangsbestand"
                          help="Aktueller Ist-Bestand der Kasse"
                          name="amount"
                          cols="12 6@tablet 4@desktop 3@hd" />
          <hm-col cols="12">
            <p>Bitte warten Sie mit der Erstellung der ersten Belege mindestens 5 Minuten, <u>nach</u> dem Erfassen den Anfangsbestands. Nur so ist sichergestellt, dass die Kasse vollständig aktiviert ist, bevor der erste Beleg erstellt wird.</p>
          </hm-col>
        </hm-card-inner>
      </template>
    </card>

    <hm-floating-buttons>
      <hm-floating-button v-if="!step1Complete && hasRole('ROLE_SYSADMIN')"
                          :throttle="30000"
                          label="Offene Folios sperren (PERMANENT)"
                          icon="lock"
                          color="red"
                          @click="showConfirmModal('Dieser Vorgang sperrt <b>unwiderrfulich</b> alle noch offenen Folios.', lockOpenFolios, 'Durch Bestätigung dieses Vorgangs werden möglicherweise steuerrelevante Daten in einem fehlerhaften Zustand dauerhaft festgeschrieben.')" />
      <hm-floating-button v-if="!step1Complete || !step2Complete || !step3Complete"
                          label="Daten aktualiserieren"
                          icon="refresh"
                          color="green"
                          @click="refreshData" />
      <hm-floating-button v-if="step1Complete && step2Complete && !step3Complete && hasRole('ROLE_ADMIN')"
                          :disabled="isNil(anfangsbestand)"
                          :throttle="300000"
                          label="Registrierkasse aktivieren"
                          icon="save"
                          color="orange"
                          @click="enableKassenSichV" />
    </hm-floating-buttons>
  </main>
</template>


<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import PageMixin from '../../mixins/PageMixin';
import StatusMixin from '../../mixins/StatusMixin';
import { isEmpty, isNil, sortBy } from 'lodash';
import Card from '../lib/layout/Card';
import FolioTable from '../lib/table/FolioTable';
import HmCardInner from '../lib/layout/HmCardInner';
import TimerMixin from '../../mixins/TimerMixin';
import { baseOldUiAddresses } from '@/lib/util/urls.js';

export default {
  name: 'PageKassenSichV',
  components: { HmCardInner, FolioTable, Card },
  timers: {
  },

  filters: {
  },
  mixins: [HotelmeisterMixin, PageMixin, StatusMixin, TimerMixin],

  data() {
    return {
      folios: [],
      fiskaltrustConfig: null,
      cashCheck: [],
      step1Complete: false,
      step2Complete: false,
      step3Complete: false,
      anfangsbestand: null,
      creating: false,
      loading: false,
    };
  },

  computed: {
  },

  created: function () {
    this.loading = true;
    this.refreshData();
  },

  mounted() {
  },

  beforeDestroy() {
  },

  methods: {
    refreshData() {
      this.updateFolios();
      this.updateFiskaltrustConfig();
      this.updateCashCheck();
    },
    updateFolios() {
      this.axios.get(baseOldUiAddresses[process.env.HOTELMEISTER_ENV]('report', `/rest/ui/backend${this.serviceUrls.report.data(this, '5953f9c5-a8a0-41cb-a456-511eb2adaab9')}`))
        .then((response) => {
          this.folios = response.data.data;
          this.step1Complete = isEmpty(this.folios);
          this.loading = false;
        })
        .catch(() => {
          this.showErrorMessage('Beim Abruf der Finanzdaten ist ein unerwarteter Fehler aufgetreten.');
          this.step1Complete = false;
          this.loading = false;
        });
    },
    updateFiskaltrustConfig() {
      this.axios.get(this.serviceUrls.interfaces.fiskaltrustDE.config(this, null))
        .then((response) => {
          if (isNil(this.config)) {
            this.fiskaltrustConfig = response.data.data;
          }
          this.step2Complete = !isNil(this.fiskaltrustConfig) && !isEmpty(this.fiskaltrustConfig.cashBoxId) && !isEmpty(this.fiskaltrustConfig.accessToken) && !isEmpty(this.fiskaltrustConfig.queueId) && this.fiskaltrustConfig.enabled === true;
        })
        .catch(() => {
          this.step2Complete = false;
        });
    },
    updateCashCheck() {
      this.axios.get(this.serviceUrls.cashBook.getCashCheck(this, this.date))
        .then((response) => {
          this.cashCheck = response.data.data;
          this.step3Complete = this.cashCheck.length > 0;
        })
        .catch(() => {
          this.step3Complete = false;
        });
    },
    enableKassenSichV() {
      this.creating = true;
      this.axios.post(
        this.serviceUrls.registrierkasse.de.activate(this),
        {
          anfangsbestand: this.anfangsbestand,
        },
      )
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
          this.setTimeout(() => {
            this.$router.push({ name: 'registrierkasse.de' });
          }, 3500);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        })
        .finally(() => {
          this.creating = false;
          this.refreshData();
          this.$serviceBus.$emit('hotelSettings.update.immediate');
        });
    },
    lockOpenFolios() {
      this.axios.post(this.serviceUrls.registrierkasse.de.lockOpenFolios(this))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        })
        .finally(() => {
          this.refreshData();
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/pages/PageKassenSichV.vue